import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Headset,
  EnvelopeSimple,
  PaperPlaneTilt,
  Plus,
  ArrowDown,
} from "@phosphor-icons/react";
import { submitSupportTicket } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import HaveAQuestionBadge from "@/components/HaveAQuestionBadge";

// Public contact form. Same Phase-C submitSupportTicket() backend; new style.
const ISSUE_TYPES = [
  { value: "bug", label: "I found a bug" },
  { value: "feature", label: "Feature request" },
  { value: "billing", label: "Billing question" },
  { value: "account", label: "Account help" },
  { value: "other", label: "Something else" },
];

const FAQS = [
  {
    q: "I've accidentally deleted a project, can you help?",
    a: "Yes — Pro and Team tiers have version history that snapshots every change. Free tier keeps a 7-day soft-delete window. Email us your project ID and we'll restore.",
  },
  {
    q: "How often is the app updated?",
    a: "Weekly. Sometimes daily when there's a clearly-on-fire bug. The PM does the changelog because the PM does everything.",
  },
  {
    q: "Where can I download the app?",
    a: "It's a web app — no install. Works in Chrome / Safari / Firefox / Edge. Mobile-friendly but really shines on a desktop screen with a 4-date project.",
  },
  {
    q: "I'm having trouble logging in. What should I do?",
    a: "Try 'Forgot password' first (link on the login page). If that doesn't help, email us via the contact form with the email on the account and we will unstick it.",
  },
  {
    q: "How do I export?",
    a: "Project view → Export menu → pick PDF / PNG / CSV / XLSX. The export button was built before the import button on purpose. Your data, your call.",
  },
];

export default function Contact() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [issue, setIssue] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !message.trim()) {
      toast.error("Email and message are required");
      return;
    }
    setSubmitting(true);
    try {
      // Backend SupportTicketCreate expects {issue_type, description}. We don't
      // have first/last name/phone fields on the backend schema, so we fold
      // that metadata into the description string so it lands in the email
      // body the support inbox actually receives.
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim() || "Anonymous";
      const header = `From: ${fullName} <${email.trim()}>${phone.trim() ? ` / ${phone.trim()}` : ""}`;
      await submitSupportTicket({
        issue_type: issue || "other",
        description: `${header}\n\n${message.trim()}`,
      });
      toast.success("Got it — we'll reply within 1–3 business days.");
      setFirstName(""); setLastName(""); setEmail(""); setPhone(""); setIssue(""); setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit — try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen tg-public-bg">
      <PublicNav />

      {/* HERO */}
      <section className="mx-auto max-w-[1600px] px-4 pt-16 pb-12 md:pt-24">
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700 mb-6 tg-shadow-soft">
            <Headset size={11} weight="fill" className="text-indigo-500" />
            Need some help?
          </span>
          <h1 className="text-5xl md:text-7xl tracking-tight leading-[1.05] text-slate-950">
            Talk to <span className="tg-gradient-text">a person.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            We're prepared to answer your questions… in long, unfocused,
            slightly-too-honest paragraphs. Because that's how we do passion.
            Pick a side-card below or just yell into the form.
          </p>
        </div>
      </section>

      {/* CONTACT GRID */}
      <section className="mx-auto max-w-[1600px] px-4 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6 items-start">
          {/* Left side cards */}
          <div className="space-y-5">
            <SideCard
              icon={Headset}
              tone="cyan"
              eyebrow="Help yourself"
              title="Quick answers, no inbox required"
              copy="Most questions get answered faster in our FAQ — refunds, plan changes, exporting data, the works."
              cta={
                <a href="#faq" data-testid="contact-browse-faq-cta">
                  <Button className="h-11 rounded-full bg-slate-950 hover:bg-slate-800 text-white border-0 text-sm font-medium gap-2">
                    Browse the FAQ
                    <ArrowDown size={14} weight="bold" />
                  </Button>
                </a>
              }
            />
            <SideCard
              icon={EnvelopeSimple}
              tone="indigo"
              eyebrow="Email"
              title="Or send a longer note"
              copy="One of our (one) team members will reply within 1–3 business days. We appreciate your patience."
              cta={
                <a
                  href="mailto:justin.ce.inc@gmail.com?subject=ThatGantt%20%E2%80%94%20note%20from%20the%20website"
                  data-testid="contact-mailto-cta"
                >
                  <Button className="h-11 rounded-full tg-gradient hover:opacity-95 text-white border-0 text-sm font-medium gap-2">
                    Send a note
                    <PaperPlaneTilt size={14} weight="bold" />
                  </Button>
                </a>
              }
            />
          </div>

          {/* Form */}
          <form
            id="contact-form"
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl border border-slate-200 p-8 tg-shadow-soft"
            data-testid="contact-form"
          >
            <h2 className="text-3xl text-slate-950 mb-1">Send us a message</h2>
            <p className="text-sm text-slate-500 mb-6">Fields with • are required.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="First name">
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name…"
                  className="rounded-xl h-11"
                  data-testid="contact-first-name"
                />
              </Field>
              <Field label="Last name">
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name…"
                  className="rounded-xl h-11"
                  data-testid="contact-last-name"
                />
              </Field>
              <Field label="Email •">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="rounded-xl h-11"
                  data-testid="contact-email"
                />
              </Field>
              <Field label="Phone (optional)">
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 1234567"
                  className="rounded-xl h-11"
                  data-testid="contact-phone"
                />
              </Field>
            </div>

            <Field label="Issue type" className="mt-4">
              <Select value={issue} onValueChange={setIssue}>
                <SelectTrigger className="rounded-xl h-11" data-testid="contact-issue">
                  <SelectValue placeholder="Pick one…" />
                </SelectTrigger>
                <SelectContent>
                  {ISSUE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Your message •" className="mt-4">
              <Textarea
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message…"
                rows={6}
                className="rounded-xl resize-none"
                data-testid="contact-message"
              />
            </Field>

            <Button
              type="submit"
              disabled={submitting}
              className="mt-6 h-12 px-7 rounded-full tg-gradient hover:opacity-95 text-white border-0 text-base font-medium gap-2"
              data-testid="contact-submit"
            >
              {submitting ? "Sending…" : "Send"}
              <PaperPlaneTilt size={16} weight="bold" />
            </Button>
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white border-y border-slate-200 py-20">
        <div className="mx-auto max-w-[1600px] px-4">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">
              Question → Answer
            </div>
            <h2 className="text-4xl md:text-5xl text-slate-950">
              Or save us both <span className="tg-gradient-text">some email.</span>
            </h2>
            <p className="mt-4 text-slate-600">
              Our support team doesn't have 'scripts.' We have 'grievances.' Pick a card or just scream into the void; we're already there
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-3" data-testid="contact-faqs">
            {FAQS.map((f) => (
              <details
                key={f.q}
                className="group rounded-2xl bg-white border border-slate-200 p-5 transition-colors hover:border-slate-300"
                data-testid={`contact-faq-${f.q.slice(0, 20).replace(/[^a-z]/gi, "-").toLowerCase()}`}
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="text-base font-semibold text-slate-950">{f.q}</span>
                  <Plus size={14} weight="bold" className="text-slate-400 group-open:rotate-45 transition-transform" />
                </summary>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <PublicFooter />
      <HaveAQuestionBadge />
    </div>
  );
}

// ---------- helpers ----------

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SideCard({ icon: Icon, tone, eyebrow, title, copy, cta }) {
  const toneMap = {
    cyan: "bg-gradient-to-br from-cyan-400 to-blue-500 text-white",
    indigo: "bg-gradient-to-br from-violet-500 to-indigo-600 text-white",
  };
  return (
    <div className={`rounded-3xl p-7 tg-shadow-soft ${toneMap[tone]}`}>
      <Icon size={24} weight="fill" className="text-white/90 mb-4" />
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/70 mb-1">{eyebrow}</div>
      <div className="text-2xl mb-2 text-white">{title}</div>
      <p className="text-sm text-white/85 leading-relaxed mb-5">{copy}</p>
      {cta}
    </div>
  );
}
